import { FieldArray, useFormikContext } from "formik";
import React, { useState } from "react";
import { IQuoteSettings } from "../../interfaces/form/IQuoteSettings";
import {
    generateClientSettingsSummaryString,
    getClientNumberDisplay,
    sortClientsForAdult
} from "../../../common/shared/clientHelpers";
import { Unarray } from "../../types/UtilityTypes";
import ClientSettingsBox, {
    IClientSettingsBoxProduct
} from "../Clients/ClientSettingsBox/ClientSettingsBox";
import ProductChildNotEligible from "./ProductChildNotEligible";
import ProductSettingsClientWrapper from "./ProductSettingsClientWrapper/ProductSettingsClientWrapper";
import cx from "classnames";
import styles from "./BaseProductSettings.module.scss";
import SettingsDisplayType from "../../enums/SettingsDisplayType";
import { ClientProductTypes } from "../../../common/interfaces/enums/ProductType";

interface IBaseProductSettingsProps {
    productType: ClientProductTypes;
    style: SettingsDisplayType;
    className?: string;
    isChildClientAllowed?: boolean;
    children: (arg: {
        index: number;
        name: string;
        setClient: IClientSettingsBoxProduct["setClient"];
    }) => React.ReactNode;
}

const BaseProductSettings: React.FC<IBaseProductSettingsProps> = (
    props
): React.ReactElement => {
    const { productType, className, isChildClientAllowed, children } = props;
    const style = props.style || SettingsDisplayType.Card;
    const {
        errors,
        values,
        initialValues,
        setValues,
        setFieldValue,
        setFieldTouched,
        isSubmitting
    } = useFormikContext<IQuoteSettings>();

    // classes
    const getClassName = (): string => {
        return cx("padding-2xl", {
            ["panel-card"]: style === SettingsDisplayType.Card
        });
    };

    // state
    const [isOpenAtClient, setIsOpenAtClient] = useState<number>(0);

    const childIndex = values.clients.findIndex((s) => s.isChild);

    function openClient(index: number): void {
        // If we have any errors in the current client, then we don't open anything
        const clientErrors = errors?.clients?.[isOpenAtClient];
        if (clientErrors && typeof clientErrors === "object") {
            return;
        }

        // If it's already open, then we are trying to close.
        // If it's not the first client, then we open the first client box.
        if (isOpenAtClient === index) {
            const openAt = values.clients.findIndex(
                (item, itemIndex) =>
                    itemIndex !== index && (!item.isChild || itemIndex === childIndex)
            );
            return openClient(openAt);
        }

        setIsOpenAtClient(index);
    }

    function selectClient(index: number, isSelected: boolean): void {
        setFieldValue(`clients.${index}.${productType}.isSelected`, isSelected);
        setFieldTouched(`clients.${index}.${productType}.isSelected`, true, true);

        if (isSelected && style === SettingsDisplayType.Accordion) {
            setIsOpenAtClient(index);
        }
    }

    // All clients are selected if none of them already have settings.
    // If we only have a single client at all we also enforce it to be selected.
    const enableAllClients = initialValues.clients.length <= 1;

    initialValues.clients = initialValues.clients.map((item) => {
        return {
            ...item,
            isSelected:
                (enableAllClients &&
                    ((!isChildClientAllowed && !item.isChild) || isChildClientAllowed)) ||
                item[productType].isSelected
        };
    });

    const renderChildrenContainer = (): JSX.Element => {
        const numOfChildren = values.clients.filter((c) => c.isChild).length;
        const subTitle = `${numOfChildren} ${numOfChildren > 1 ? "children" : "child"}`;

        return (
            <ClientSettingsBox
                key={`client-child`}
                name={`children.${productType}.isSelected`}
                className={getClassName()}
                title="Children"
                subtitle={subTitle}
                isSelected={values.clients[childIndex]?.[productType].isSelected}
                isSelectable
                isOpenable={style === SettingsDisplayType.Accordion ? true : undefined}
                isOpen={!isSubmitting && isOpenAtClient === childIndex}
                onOpen={() => {
                    openClient(childIndex);
                }}
                onSelect={(isSelected) => {
                    selectClient(childIndex, isSelected);
                }}>
                {children({
                    index: childIndex,
                    name: `clients.${childIndex}`,
                    setClient: (client) => {
                        const newVals = { ...values };

                        newVals.clients = newVals.clients.map((c) => {
                            if (c.personId === client.personId) {
                                return client;
                            }
                            return c;
                        });

                        setValues(newVals);
                    }
                })}
            </ClientSettingsBox>
        );
    };

    return (
        <ProductSettingsClientWrapper
            className={cx({
                [className]: !!className,
                ["flex-gap-m"]: style === SettingsDisplayType.Card,
                [styles["style-accordion-wrapper"]]:
                    style === SettingsDisplayType.Accordion ||
                    style === SettingsDisplayType.List
            })}
            error={typeof errors.clients === "string" && errors.clients}>
            <FieldArray name="clients">
                {({ replace }) =>
                    values.clients
                        .sort(sortClientsForAdult)
                        .filter((c) => !c.isChild) // We treat Children as a block, so we don't want don't want individual settings
                        .map((client, index) => {
                            const setClient = (
                                client: Unarray<typeof initialValues["clients"]>
                            ): void => replace(index, client);

                            const boxTitle = `${
                                client.isChild ? "Child" : "Adult"
                            } ${getClientNumberDisplay(index, values.clients, client)}`;

                            const subTitle = generateClientSettingsSummaryString(client);

                            const name = `clients.${index}`;

                            return (
                                <ClientSettingsBox
                                    id={name}
                                    name={`${name}.${productType}.isSelected`}
                                    className={getClassName()}
                                    key={`${name}-${client.personId}`}
                                    title={boxTitle}
                                    subtitle={subTitle}
                                    isSelected={client[productType]?.isSelected}
                                    isSelectable={
                                        values.clients.filter((s) => !s.isChild).length >
                                        1
                                    }
                                    isOpenable={style === SettingsDisplayType.Accordion}
                                    isOpen={!isSubmitting && isOpenAtClient === index}
                                    onOpen={() => openClient(index)}
                                    onSelect={(isSelected) => {
                                        selectClient(index, isSelected);
                                    }}>
                                    {children({
                                        index,
                                        name: `clients.${index}`,
                                        setClient
                                    })}
                                </ClientSettingsBox>
                            );
                        })
                }
            </FieldArray>
            {isChildClientAllowed && values.clients.some((s) => s.isChild)
                ? renderChildrenContainer()
                : style === SettingsDisplayType.Card && (
                      <ProductChildNotEligible
                          className={getClassName()}
                          allowChildren={isChildClientAllowed}
                          clients={initialValues.clients}
                      />
                  )}
        </ProductSettingsClientWrapper>
    );
};

export default BaseProductSettings;
